import { Fragment, useEffect, useMemo, useRef, useState } from 'react'

import type { CalendarEntry } from '~/api/types'

import s from './ListView.module.css'

import type { Density } from './views'

import { useArtwork } from '~/api/artwork'
import { bubbleFor } from '~/components/bubbles'
import { AppLink } from '~/components/AppLink'

import { mondayOf } from '~/calendar/address'
import { bill, EventCard } from '~/components/EventCard'
import { Faces } from '~/components/Faces'
import { useMarks } from '~/components/MarksContext'
import { heldByStar, onList, spread, tallyLine, unspread, whoIs } from './views'
import { Bookmark } from '~/components/Bookmark'
import { Star } from '~/components/Star'

/**
 * The list, ported from `_calendar_page.py` — `renderList`, `days`, `row`, `card`,
 * `watchTail`, `listMark`, `money` and `short`.
 *
 * Gigs grouped by night, the day on a rail down the left and what is on beside it, in two
 * densities: `compact` is one row per gig with the columns aligned down the page, `full` is
 * a grid of cards with the artwork on them.
 *
 * There are no links here on purpose, so nothing goes through `AppLink`. A row opens the
 * event sheet the parent owns and a venue filters the calendar in place — both of which the
 * Python does with `data-` attributes and one delegated listener, and both of which are
 * callbacks here.
 */

/**
 * The signed-in viewer. Local, because all this component asks is whether there is one:
 * every control below posts, and a signed-out reader's click ends at the login page.
 * `Viewer` in `~/api/types` is the same shape and is what a caller will already be holding.
 */
export interface Me {
  id: number
  name: string
  username: string
  isEditor: boolean
}

/**
 * Tonight, on the London wall clock.
 *
 * `days()` reads `DATA.today`, which the server computes and ships on the payload; the props
 * this component was given do not carry it. Derived from the browser here, which agrees with
 * the server except for a reader whose machine is wrong. Built from parts rather than from
 * an `en-CA` string so no locale can reorder the fields.
 */


/**
 * The price, in pounds, floored, and prefixed.
 *
 * **Feed prices are not box-office prices.** They arrive converted, or fee-inclusive, or both,
 * and printing them to the penny put £23.27, £36.47 and £48.40 down a column — numbers no
 * London door ever charged, which make the whole column read as machine output rather than as
 * something a person checked. Flooring to the pound and saying "from" is the only honest
 * reading of a number that is a lower bound anyway: what the cheapest ticket was when the feed
 * last spoke.
 *
 * The exact figure is still on the sheet, beside the seller who is accountable for it. Here,
 * where a reader is deciding whether a night is affordable, the pound is the whole answer.
 *
 * Exported for the share cards, which print the same floored pound: a third copy of this
 * rule would be one more than `Briefing.tsx` already made too many. The parameter is only
 * the field it reads, so a stranger's row — no editorial fields — passes as readily as an
 * editor's.
 */
export function money(e: Pick<CalendarEntry, 'priceMin'>): string {
  if (e.priceMin == null) return ''
  // A range collapses to its floor too: "from £12" already says there are dearer tickets, so
  // "from £12–£28" says it twice and spends the column's width doing it.
  return `from £${Math.floor(e.priceMin / 100)}`
}

/** Cut to length, at a bill separator where there is one, so the result reads as names
 *  rather than as a word sliced in half. */
function short(value: string | null, n: number): string {
  const text = String(value ?? '')
  if (text.length <= n) return text
  const cut = text.slice(0, n)
  const at = cut.lastIndexOf(' + ')
  return (at > n / 2 ? cut.slice(0, at) : cut.trimEnd()) + ' …'
}

/**
 * What the heart means, in one place, because it was three different things in five.
 *
 * A gig is on the list two ways that are not a personal save: the editor added it, or a
 * starred act headlines it. Both are `picked`, and the heart is on for both.
 */


/**
 * The decision on the row, and whether it has already started.
 *
 * `past` sits alongside the decision rather than replacing it: a gig you are going to that
 * happened this afternoon is still one you are going to. And what the heart means is what
 * the highlight means — every view once drew its highlight from `liked` alone, so a gig the
 * editor added but nobody had personally hearted came out with a filled heart and no colour
 * on the row.
 */
function marked(e: CalendarEntry, base: string, me: Me | null): string {
  const mark = e.going ? s.going : onList(e, me) ? s.picked : ''
  return [base, mark, e.past ? s.past : ''].filter(Boolean).join(' ')
}

/* ── the marks ─────────────────────────────────────────────────────────────────────── */

/*
 * Drawn, not typed. `♥` is a text glyph: at the 12px these sit at the font hints it into a
 * wedge, and which wedge depends on the platform's emoji font. A path is the same shape at
 * every size and on every machine.
 */

function Tick({ fill }: { fill: boolean }) {
  return (
    <svg
      className={s.mark}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={fill ? 3.2 : 2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 12.5 9.5 18 20 6" />
    </svg>
  )
}

/**
 * The compact row's heart: this reader's decision about this night.
 *
 * One cell, always emitted. The row is a grid of fixed tracks and every mark owns one, so
 * a row with nothing to press still holds the track open — an absent cell would slide the
 * venue, the tags and the price one column left on that row alone.
 */
function ListMark({
  e,
  me,
  onSave,
}: {
  e: CalendarEntry
  me: Me | null
  onSave: (id: string, on: boolean) => void
}) {
  // Nothing to press when there is nobody to press it. Every one of these controls posts,
  // and a signed-out reader's click ends at the login page — so the calendar was offering a
  // decision it could not take and then asking for an account to explain itself.
  if (!me) return <span className={`${s.mini} ${s.blank}`} aria-hidden="true" />
  const on = onList(e, me)
  return (
    <button
      type="button"
      className={`${s.mini} ${s.heartmark}`}
      aria-pressed={on}
      title={on ? 'Curated' : 'Add to Curated'}
      onClick={ev => {
        ev.stopPropagation()
        onSave(e.id, !on)
      }}
    >
      <Bookmark fill={on} className={s.mark} />
    </button>
  )
}

/**
 * The star, in a track of its own, where an act you follow is what put this on the list.
 *
 * The star *and* the heart, not one or the other. They were mutually exclusive: a gig held
 * on the list by an act you follow drew the star in the heart's place, so the one thing you
 * could not do to it was decide about it. They answer different questions and both belong —
 * the star is about the act and every night they play, the heart is about this night.
 *
 * Its own cell, and a blank one on every row that has no star. It used to be a second
 * child squeezed in beside the heart, which made a star-held row one cell longer than the
 * grid has tracks: the price wrapped and every column from the heart on shifted one track
 * right on that row alone. A fixed grid is only fixed if every row fills the same cells.
 */
function StarSlot({ e, me }: { e: CalendarEntry; me: Me | null }) {
  // The same actions the cards use. Read here rather than threaded down as a prop, because
  // the star is the one mark on this row that the row's own props never carried.
  //
  // `useMarks` answers null outside a provider — so the row keeps working either way, and
  // without one the slot is blank rather than a control that cannot act.
  const star = useMarks()?.star
  const held = me ? heldByStar(e) : null
  if (!held || !star) {
    return <span className={`${s.mini} ${s.blank} ${s.lstar}`} aria-hidden="true" />
  }
  return (
    // It unfollows them, which takes every one of their nights off rather than only this
    // one — and it is a control rather than the `role="img"` label it was, now that
    // `MarksContext` gives it something to call.
    <button
      type="button"
      className={`${s.mini} ${s.starmark} ${s.lstar}`}
      aria-pressed="true"
      aria-label={`On the list because you follow ${held.name}. Unfollow them`}
      title={`On the list because you follow ${held.name}`}
      onClick={ev => {
        ev.stopPropagation()
        star(held.id, false)
      }}
    >
      <Star fill className={s.mark} />
    </button>
  )
}

/* ── the tags ──────────────────────────────────────────────────────────────────────── */

interface Tag {
  key: string
  tone: 'go' | 'friends' | 'pick' | 'warn' | 'held' | null
  text: string
  /** What a hover says. The friends tag counts; the names are the answer to "who". */
  said?: string
}


/**
 * A compact row's tags.
 *
 * **Nothing is said about being on the list.** Every row on the Curated tab is on the list —
 * that is what the tab is — so printing it against each one spent the row's single line of
 * tags restating the heading above them, several hundred times down a page. What that line is
 * for is the things only this row knows: that you are going, that a friend is, that it has been
 * cancelled, that the act plays other nights. Where a gig is on the list and none of those is
 * true, the row says nothing, and silence means picked.
 */
function rowTags(e: CalendarEntry): Tag[] {
  const tags: Tag[] = []
  /*
   * Nothing about the friends, in words. The row draws them as faces in a track of their
   * own — going lit, saved dimmed, `+N` for the rest — so a "1 going" beside the one face
   * of the one who is going says the same fact twice on the one line the row has. On a
   * phone that line also holds the time, the room and the price, and the count was what
   * pushed the room down to a letter and an ellipsis. Who they are is the stack's `title`.
   *
   * Your own mark stays: the faces are other people, and the tick beside them is yours.
   */
  if (e.going) tags.push({ key: 'going', tone: 'go', text: e.past ? 'you were there' : 'going' })
  else {
    // Why *this* row, where the answer is about the reader rather than about the gig. The
    // editor's pick is not a reason worth printing — every row on the Curated tab has it, so
    // saying so is the heading again. An act you follow is a reason only this row has.
    const held = heldByStar(e)
    if (held) tags.push({ key: 'star', tone: 'held', text: `you follow ${held.name}` })
  }
  if (e.lifecycle !== 'scheduled') tags.push({ key: 'life', tone: 'warn', text: e.lifecycle })
  if (e.otherDates > 0) tags.push({ key: 'dates', tone: null, text: `+${e.otherDates}` })
  return tags
}

/** A card's tags. It has the room for the long forms and for the price, which the row prints
 *  in a column of its own. */
function cardTags(e: CalendarEntry): Tag[] {
  const tags: Tag[] = []
  if (e.going) tags.push({ key: 'going', tone: 'go', text: 'going' })
  if (e.friends.length)
    tags.push({
      key: 'friends',
      tone: 'friends',
      text: tallyLine(e.friends, { past: e.past }),
      said: whoIs(e.friends, { past: e.past }),
    })
  if (e.lifecycle !== 'scheduled') tags.push({ key: 'life', tone: 'warn', text: e.lifecycle })
  if (e.otherDates > 0) {
    tags.push({
      key: 'dates',
      tone: null,
      text: `+${e.otherDates} more date${e.otherDates > 1 ? 's' : ''}`,
    })
  }
  if (e.type === 'festival') tags.push({ key: 'type', tone: null, text: 'festival' })
  // No price on a night that is not happening. The accent means money and anything you can
  // act on, so a glowing "from £13" beside CANCELLED is the law inverted: it is the loudest
  // thing on a row whose whole news is that there is nothing to buy.
  const price = e.lifecycle === 'scheduled' ? money(e) : ''
  if (price) tags.push({ key: 'price', tone: null, text: price })
  return tags
}

function tagClass(tag: Tag): string {
  return tag.tone ? `${s.tag} ${s[tag.tone]}` : s.tag
}

/* ── the two densities ─────────────────────────────────────────────────────────────── */

interface ItemProps {
  e: CalendarEntry
  me: Me | null
  onOpen: (id: string) => void
  onSave: (id: string, on: boolean) => void
  onGoing: (id: string, on: boolean) => void
}

/** The rest of the bill as one line, or "" when the headliner is the whole of it. */
/** "Thursday 21 August", from a night. */
function dayLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  return d.toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  })
}

function monthLabel(iso: string): string {
  const d = new Date(`${iso}T12:00:00Z`)
  return d.toLocaleDateString('en-GB', { timeZone: 'UTC', month: 'long', year: 'numeric' })
}

/**
 * Whether two names are the same name.
 *
 * Not equality: the brand is `Horse Meat Disco` and the headline `Horse Meat Disco at Eagle
 * London`, so the question is whether one already says the other. Case and punctuation are
 * dropped because a listing writes `Rough Trade` and a brand `ROUGH TRADE.` for the same
 * thing.
 */
function sameName(a: string, b: string): boolean {
  const flat = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  const [x, y] = [flat(a), flat(b)]
  return x === y || x.startsWith(y) || y.startsWith(x)
}

function support(e: CalendarEntry): string {
  const { more, rest } = bill(e.title, e.bill)
  if (more.length === 0) return rest > 0 ? `+ ${rest} more` : ''
  return rest > 0 ? `${more.join(' + ')} + ${rest} more` : more.join(' + ')
}

/**
 * Whether this click was the row itself rather than something inside it that already navigates.
 *
 * The row is a `<div onClick>` with links in it, so a tap on a link fired both: the anchor
 * navigated and the row called `onOpen`, to the same address. Two history entries for one tap
 * — and `Overlay.shut` closes with `navigate(-1)`, which then lands on the *duplicate*. The
 * route still matches, so the sheet stays mounted with its dialog shut and every later tap
 * changes the address while nothing draws. That is the whole of "clicking an event does
 * nothing, and refreshing shows it".
 *
 * Asked of the event rather than fixed with `stopPropagation` on each link, because the links
 * are the part that grows: four of the compact row's five clickable descendants already
 * stopped propagation and the act name did not, so the same row behaved differently depending
 * on which half you hit. A sixth would have to remember, and the card wrapper below holds
 * four more.
 */
function openedTheRow(ev: React.MouseEvent): boolean {
  return !(ev.target as HTMLElement).closest('a,button')
}

/**
 * The gig's own picture, at the head of its row.
 *
 * A list of names is a list a reader has to *read*; a poster is recognised before it is read,
 * and on a phone — where the row has one line for the name and one for everything else —
 * recognition is most of what the row can offer. The card views have always carried it and
 * the compact row did not, which made the densest view also the hardest to scan.
 *
 * Graded like every other picture on the site (`saturate .55`, `brightness .88`), so forty
 * promoters' press shots hang as one wall rather than as forty. Where there is no artwork —
 * about one gig in six — the bubble the cards fall back to, keyed off the id so the same gig
 * keeps the same one and two rows running are never twins.
 *
 * `aria-hidden` and not a link: the name beside it already goes to the gig, and a second
 * anchor to the same address is one more stop for a screen reader and nothing for anybody
 * else.
 */
function Thumb({ e }: { e: CalendarEntry }) {
  const { src, broken, onError } = useArtwork(e.image)
  return (
    <span className={s.lshot} aria-hidden="true">
      {src && !broken ? (
        <img src={src} alt="" loading="lazy" decoding="async" onError={onError} />
      ) : (
        <img src={bubbleFor(e.id)} alt="" loading="lazy" decoding="async" className={s.lfallback} />
      )}
    </span>
  )
}

function Row({ e, me, onOpen, onSave, onGoing }: ItemProps) {
  const tags = rowTags(e)
  return (
    // `data-id` and `data-lifecycle` are the pair the Python's "what changed since you last
    // looked" pass reads back off the DOM. Kept so that pass still has its handle.
    <div
      className={marked(e, s.lrow, me)}
      data-id={e.id}
      data-lifecycle={e.lifecycle}
      onClick={ev => openedTheRow(ev) && onOpen(e.id)}
    >
      <Thumb e={e} />
      <ListMark e={e} me={me} onSave={onSave} />
      {me ? (
        <button
          type="button"
          className={`${s.mini} ${s.goingmark}`}
          aria-pressed={e.going}
          // The one control the Python ships here without a title, though the month chip and
          // the card both give it one. Restored: its only content is an `aria-hidden` glyph,
          // so without this the button has no accessible name at all.
          title={
              e.past
                ? e.going
                  ? 'You were there'
                  : 'Mark that you were there'
                : e.going
                  ? "You're going"
                  : 'Mark as going'
            }
          onClick={ev => {
            ev.stopPropagation()
            onGoing(e.id, !e.going)
          }}
        >
          <Tick fill={e.going} />
        </button>
      ) : (
        <span className={`${s.mini} ${s.blank}`} aria-hidden="true" />
      )}
      <StarSlot e={e} me={me} />
      {/*
        * Two lines: who is headlining, and who else is on.
        *
        * One line held the whole title — `A + B + C + D` truncated at 66 characters — so the
        * name you were looking for was in the middle of a run of grey and the row's most
        * important fact had the same weight as its least. The split is the card's own, from
        * `EventCard`, so the compact row and the card agree about what a headliner is and
        * about counting the remainder off the bill rather than off the title.
        */}
      {/*
        * The name is the link. The row cannot be one — it holds the marks, the going tick
        * and the venue link, and an anchor may not contain any of them — but a gig on this
        * page still has to be cmd-clickable, middle-clickable and copyable, which is the
        * whole of the bug. So the anchor is the thing a reader would actually aim at, and
        * the row's own click stays as the larger target it always was.
        */}
      <AppLink className={s.lname} title={e.title} to={`/e/${e.id}`} seed={unspread(e)}>
        <span className={s.lhead}>
          {/*
            * The brand, unless the brand is the title.
            *
            * `Horse Meat Disco at Eagle London` is a club night whose listing name and whose
            * festival name are the same string, so the row drew it twice — once muted and
            * truncated, once bold and truncated — and read as a rendering fault rather than
            * as a brand. The chip is only worth its width when it says something the
            * headline beside it does not.
            */}
          {e.festival && !sameName(e.festival, bill(e.title, e.bill).headline) ? (
            <span className={s.fest}>{short(e.festival, 24)}</span>
          ) : null}
          {bill(e.title, e.bill).headline}
        </span>
        {support(e) ? <span className={s.lsupport}>{support(e)}</span> : null}
      </AppLink>
      <span className={s.lmeta}>
        {/*
          * The friends on it. Inside `.lmeta` because on a phone that is the second line
          * and the faces lead it; on the desktop `.lmeta` is `display: contents`, so this
          * is a cell of the row's own grid and the stylesheet orders it into the faces
          * track beside the thumb. One element, two layouts, no second stack.
          *
          * A blank of the same class when nobody is on it, so the track is filled on every
          * row and the columns after it stay put.
          */}
        {e.friends.length ? (
          <Faces
            className={s.lfaces}
            friends={e.friends}
            size={18}
            past={e.past}
            to={`/e/${e.id}`}
          />
        ) : (
          <span className={s.lfaces} aria-hidden="true" />
        )}
        <span className={s.ltime}>{e.time}</span>
        {/*
          * A link to the room, not a button that filters the calendar.
          *
          * The Python's `pickVenue` did the second: choosing a venue here switched the tab
          * to Everything, the grid to a compact list, and filtered — while the same name on
          * the front page opened `/v/<slug>`. One control, two meanings, on two halves of
          * one site, and the calendar's half could not be sent to anybody.
          *
          * The room's page is the better of the two and the one the front page already
          * used: it carries the address, the map and their own listings, which a filter
          * cannot. Filtering is still what the venue finder is for.
          *
          * `stopPropagation` because the row underneath opens the gig.
          */}
        {e.venueSlug !== null ? (
          <AppLink
            className={`${s.lvenue} ${s.venuelink}`}
            title={e.venue}
            to={`/v/${e.venueSlug}`}
            onClick={ev => ev.stopPropagation()}
          >
            {e.venue}
          </AppLink>
        ) : (
          // A placeholder room has no page. The name is still what the listing says.
          <span className={s.lvenue} title={e.venue}>
            {e.venue}
          </span>
        )}
        <span className={s.ltags}>
          {tags.map((tag, i) => (
            <Fragment key={tag.key}>
              {i > 0 ? ' ' : null}
              <span className={tagClass(tag)} title={tag.said}>
                {tag.text}
              </span>
            </Fragment>
          ))}
        </span>
        <span className={s.lprice}>{e.lifecycle === 'scheduled' ? money(e) : ''}</span>
      </span>
    </div>
  )
}

function Card({ e, onOpen }: ItemProps) {
  /*
   * The front page's card, with the calendar's controls in its slot.
   *
   * There were two. This one had drifted to a different shape entirely — its own artwork
   * treatment, its own type scale, its own idea of what a bill line is — while the front
   * page's had been through five rounds of design notes. Two components with one job, and
   * only one of them was being looked at.
   *
   * `CalendarEntry` is a structural superset of `LandingEvent` now, which is what made this
   * possible without an adapter: the server carries `startsAt`, `timeKnown` and `bill` on
   * the row, and the card reads the same nine fields whichever half of the site it is on.
   */
  return (
    <div className={s.cardwrap} onClick={ev => openedTheRow(ev) && onOpen(e.id)}>
      <ol className={s.cardlist} data-id={e.id} data-lifecycle={e.lifecycle}>
        <EventCard
          event={e}
          // The card draws this night's "(Day n/N)" copy; the sheet opens from the row as the
          // server sent it — see `unspread`.
          seed={unspread(e)}
          tags={cardTags(e).map((tag, i) => (
            <Fragment key={tag.key}>
              {i > 0 ? ' · ' : null}
              <span className={tag.tone ? s[tag.tone] : undefined} title={tag.said}>
                {tag.text}
              </span>
            </Fragment>
          ))}
        />
      </ol>
    </div>
  )
}

/* ── the list ──────────────────────────────────────────────────────────────────────── */

interface DayGroup {
  iso: string
  /** The first gig of the night, which is where the rail's date is read from — every entry
   *  on the night carries the same one, already formatted in London by the server. */
  head: CalendarEntry
  items: CalendarEntry[]
}

function groupByDay(list: CalendarEntry[]): DayGroup[] {
  const byDay = new Map<string, DayGroup>()
  // `spread` first: a festival is on every night of its run, and a list that files it under
  // the opening night alone answers "what is on on Saturday?" with everything except the
  // thing most people are asking about. Each copy says which night it is.
  for (const e of spread(list)) {
    const group = byDay.get(e.iso)
    if (group) group.items.push(e)
    else byDay.set(e.iso, { iso: e.iso, head: e, items: [e] })
  }
  return [...byDay.values()]
}

/** The months present, in the order the entries arrive in — which is the order the server
 *  ranked them, and the order the list runs on through. */
function monthsIn(entries: CalendarEntry[]): string[] {
  // Sorted, not first-seen. The entry list arrives ranked — going first, then liked, then
  // picked — so its first row is whatever the reader has decided about, not the earliest
  // night. Taking the months in encounter order therefore drew the list out of order and
  // made the infinite-scroll window start wherever the ranking happened to lead.
  return [...new Set(entries.map(e => e.month))].sort()
}

export interface ListViewProps {
  entries: CalendarEntry[]
  /**
   * Today, as the server saw it — `YYYY-MM-DD`, London.
   *
   * This was derived here from the browser clock with a `Europe/London` formatter, in this
   * view and in two others. That is right nearly always and wrong exactly when it matters: a
   * machine a few minutes off, or a reader looking across London's midnight, gets a different
   * today from the one the calendar was built with. The night rule already cuts the day at
   * 05:00; "nearly right" is not a property this page can afford. The payload has carried it
   * all along.
   */
  today: string
  density: Density
  me: Me | null
  onOpen: (id: string) => void
  onSave: (id: string, on: boolean) => void
  onGoing: (id: string, on: boolean) => void
  /**
   * Whether the list pages forward for ever.
   *
   * True when the list is how somebody is browsing the calendar. False when it is showing
   * one night, one week or one month — paging that into September is not what pressing
   * "+4 more" on a Thursday meant, and a control that answers differently depending on how
   * you arrived is how a calendar comes to feel like it is guessing.
   */
  paging?: boolean
  /** Whether the calendar continues past the rows this list was given. */
  canExtend?: boolean
  /** True while a wider window is in flight, so the sentinel does not ask twice. */
  extending?: boolean
  /** Ask for a wider window. Called when the reader scrolls past what is loaded. */
  onExtend?: () => void
  /** What the list is confined to, and the way out. Null when it is not confined. */
  window?: { only: 'day' | 'week' | 'month'; on: string; clear: () => void } | null
  /**
   * The month the toolbar is anchored on, or null where the list is answering a window.
   *
   * The arrows moved the address and this list never read it, so stepping the month in list
   * view did nothing at all — the control was live, the label changed, and the page stood
   * still. A list is a continuous scroll rather than a set of pages, so "go to October" is
   * not a different list: it is this one, opened far enough to reach October, scrolled to
   * where October starts.
   */
  anchor?: string | null
}

export function ListView({
  entries,
  density,
  me,
  today,
  onOpen,
  onSave,
  onGoing,
  paging = true,
  canExtend = false,
  extending = false,
  onExtend,
  window: only = null,
  anchor = null,
}: ListViewProps) {
  const months = useMemo(() => monthsIn(entries), [entries])

  // The list runs on: the window grows as you reach the bottom rather than the month arrows
  // paging you through it. A calendar is a continuous thing, and stopping at a month boundary
  // made you click to find out whether next week was empty.
  const [shown, setShown] = useState(1)

  /**
   * Back to one month whenever the list itself changes — a venue is picked, a tab is
   * switched — but *not* when it merely grows.
   *
   * Keyed on the months rather than on the array, because a parent that rebuilds its
   * filtered list on every render would otherwise undo each widening the moment it
   * happened, and the sentinel below would spin against it.
   *
   * The prefix test is what the server-side window added. Scrolling to the bottom now
   * fetches more months and appends them, so `months` legitimately changes while the list
   * is standing still; resetting on that would snap the reader back to August every time
   * they reached the end of it. A list that has only grown starts with the months it
   * already had.
   */
  // While `start` stands still, only the months from `start` on are compared, because those
  // are the only ones the list can open — `from` below skips everything earlier. Rows
  // landing in a month before `start` change nothing drawn, and compared on every month they
  // read as a different list and closed the months the reader had opened: a list anchored
  // on the month holding `/entries`' `since` drew October, then the claimed body landed
  // with June and August and took October away. When `start` moves — the anchor stepping —
  // every month is compared, which is the rule this had before `start` was part of it.
  const start = [anchor?.slice(0, 7), today.slice(0, 7)].filter(Boolean).sort()[0] as string
  const every = months.join(' ')
  const opening = months.filter(m => m >= start).join(' ')
  const was = useRef({ every, opening, start })
  useEffect(() => {
    const grown =
      start === was.current.start
        ? opening.startsWith(was.current.opening)
        : every.startsWith(was.current.every)
    if (!grown) setShown(1)
    was.current = { every, opening, start }
  }, [every, opening, start])

  /*
   * Stepping the month opens the list as far as that month and scrolls to it.
   *
   * Not on the first render: the list opens on the month it is already showing, and
   * scrolling to the top of a page somebody has just arrived at is a jump with no cause.
   * `stepped` holds the last anchor acted on, so only a change counts.
   *
   * Where the month is past the far edge of what the server sent, this asks for a wider
   * window instead and tries again when it arrives — which is the same widening the
   * sentinel at the bottom does, reached by a different route.
   */
  const wanted = anchor ? anchor.slice(0, 7) : null
  const stepped = useRef(wanted)
  // Whether an arrow has actually been pressed. `stepped` starts at the anchor the list
  // mounted on, so it matches on the first render and cannot tell "already here" from
  // "asked to come here" — which had the list scrolling itself the moment it appeared.
  const moved = useRef(false)
  useEffect(() => {
    if (!wanted || wanted === stepped.current) return
    const idx = months.indexOf(wanted)
    if (idx < 0) {
      if (canExtend && !extending) onExtend?.()
      return
    }
    stepped.current = wanted
    moved.current = true
    setShown(n => Math.max(n, idx + 1))
  }, [wanted, months, canExtend, extending, onExtend])

  /*
   * A window is already the whole of what it is about, so every month of it is open.
   *
   * Paging starts at the anchored month, not at the first month the payload happens to
   * hold. `months.slice(0, shown)` took the earliest — which after a scroll that fetched
   * August is August — so the Curated list opened three weeks in the past, on a night that
   * had been and gone, under a bar that said September. Worse, a window that is entirely
   * past turns the "show past shows" fold off (a control that refuses the question is worse
   * than the question), so the dead nights were not even folded away: they were the landing.
   *
   * The window starts at the earlier of **this month** and the month the reader has stepped
   * to, and runs forward. Both halves are load-bearing:
   *
   * - *this month*, so a payload that has fetched August while scrolling does not put August
   *   back at the top the next time the list is rebuilt;
   * - *the anchor, where it is earlier*, because a reader who pages deliberately back to
   *   August has asked for August and must get it.
   *
   * Forward from there is what keeps the list continuous: stepping to October widens the
   * window through September rather than jumping over it, which is the whole reason the
   * months page by growing instead of by replacing.
   */
  const from = Math.max(0, months.findIndex(m => m >= start))
  const open = paging ? months.slice(from, from + shown) : months
  const inOpenMonths = entries.filter(e => open.includes(e.month))

  /*
   * A list opens on what is still to come.
   *
   * The calendar keeps the nights that have ended so "what did I miss" and "where were my
   * friends last week" can be asked at all. A month grid and a week open on a date and the
   * past is a press of the back arrow away; a list starts at the top of its first month,
   * which is now last week — so the first thing a reader met was six days of gigs they had
   * already missed.
   *
   * Behind a control instead, and absent entirely where there is nothing behind it. The
   * exception is a window that is *itself* over: asking for a night in July and being shown
   * nothing because July has been and gone would be the control refusing the question.
   */
  const gone = inOpenMonths.filter(e => e.past)
  const [showPast, setShowPast] = useState(false)
  const wholeWindowIsPast = inOpenMonths.length > 0 && gone.length === inOpenMonths.length
  /*
   * And a list stepped back to a month that has gone is a question about that month, in the
   * same way a window that is over is: the reader pressed the arrow to look at November, and
   * folding November away behind a control is refusing that too. The rule holds as the
   * sentinel walks forward from there — it is the anchor that decides, not what the open
   * months happen to hold — so reaching today's month does not fold the way there. A list
   * anchored on today's month keeps its fold.
   */
  const anchoredBehind = !!anchor && anchor.slice(0, 7) < today.slice(0, 7)
  const unfolded = wholeWindowIsPast || anchoredBehind
  const visible = showPast || unfolded ? inOpenMonths : inOpenMonths.filter(e => !e.past)
  // More to show: either months already loaded, or months the server still has. The second
  // is what keeps an infinite list infinite now that the payload is a window.
  const more = paging && (shown < months.length || canExtend)

  /*
   * There is no opening scroll, and there was one.
   *
   * It existed because the calendar had started keeping the nights that have ended, so a
   * list opened on last week and had to be put on tonight. Past gigs sit behind "show past
   * shows" now, which means the list already opens on tonight — and a scroll to where you
   * already are is at best nothing.
   *
   * It was also the bug: it waited until it found today's row, which never resolves for a
   * list opened on a month that is not this one, so it stayed armed. The next thing to add
   * rows is "show more", and pressing that halfway down the list fired the opening scroll
   * and threw the page back to the top.
   */

  /* Put there once the month it is about is actually on the page. */
  const opened = open.join(' ')
  useEffect(() => {
    if (!moved.current || !wanted || wanted !== stepped.current || !open.includes(wanted)) return
    const first = document.querySelector<HTMLElement>(`[data-date^="${wanted}"]`)
    /*
     * Instantly, not smoothly. Stepping from August to October opens two months in between
     * and then animates the whole way down them — seconds of scrolling past gigs nobody
     * asked to see, ending somewhere the reader has to work out. A month arrow is a jump;
     * it should land like one.
     *
     * Called defensively because jsdom has no such method, and the list is opened to the
     * right month with or without it.
     */
    first?.scrollIntoView?.({ block: 'start', behavior: 'auto' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, opened])

  const tail = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const node = tail.current
    // No sentinel means everything is already on the page. And jsdom has no observer, so a
    // test renders the first month and stops there rather than throwing.
    if (!node || typeof IntersectionObserver === 'undefined') return
    // One observer, re-armed on each widening, so scrolling fast cannot stack a queue of
    // pending appends. A sentinel still in view after the append re-fires and the list keeps
    // going, which is what makes a fast flick to the bottom load more than one month.
    const watcher = new IntersectionObserver(
      es => {
        if (!es.some(x => x.isIntersecting)) return
        // Two ways to show more, in order: open another month of what is already here, or —
        // having opened them all — ask the server for a wider window. The second is the one
        // the windowed payload made necessary; without it the list ends wherever the fetch
        // happened to stop, which looks like the calendar ending.
        if (shown < months.length) setShown(n => n + 1)
        else if (canExtend && !extending) onExtend?.()
      },
      { rootMargin: '600px' },
    )
    watcher.observe(node)
    return () => watcher.disconnect()
  }, [shown, more, months.length, canExtend, extending, onExtend])

  // Named for what widening gives you, not for what it takes away. "Show the rest" is true
  // and says nothing about what the rest is; from one Thursday, what you want next is the
  // other days.
  const wider =
    only &&
    ({ day: 'Show more days', week: 'Show more weeks', month: 'Show more months' } as const)[
      only.only
    ]

  const said =
    only &&
    ({
      day: `Just ${dayLabel(only.on)}`,
      week: `Just the week of ${dayLabel(mondayOf(only.on))}`,
      month: `Just ${monthLabel(only.on)}`,
    } as const)[only.only]

  if (!visible.length) {
    return (
      <div className={s.empty}>
        {only ? (
          <>
            Nothing on {said?.replace(/^Just /, '')}.{' '}
            <button type="button" className={s.clear} onClick={only.clear}>
              {wider}
            </button>
          </>
        ) : (
          'Nothing on this calendar from here on.'
        )}
      </div>
    )
  }


  return (
    <>
      {/*
        * Only where there is something behind it. A control that reveals nothing is worse
        * than no control, and most of the calendar has no past in the months it is showing.
        */}
      {gone.length > 0 && !unfolded ? (
        <button
          type="button"
          className={s.pastbtn}
          aria-expanded={showPast}
          onClick={() => setShowPast(on => !on)}
        >
          {showPast
            ? 'Hide past shows'
            : `Show ${gone.length} past ${gone.length === 1 ? 'show' : 'shows'}`}
        </button>
      ) : null}
      <div className={s.days}>
        {groupByDay(visible).map(group => (
          <div
            key={group.iso}
            /*
             * A day is past when its date is behind today's. The rows inside already dim
             * themselves off `e.past`, and the date in the margin did not — so a night that
             * had been and gone kept a bright white numeral over a column of faded gigs,
             * which read as the day being current and its gigs being unavailable rather than
             * as the whole night having happened.
             *
             * Off the group's own date rather than off `every(e => e.past)`: a day is behind
             * us or it is not, and asking the gigs would make an empty day undecidable.
             */
            className={[
              s.day,
              group.iso === today ? s['is-today'] : '',
              group.iso < today ? s['is-past'] : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-date={group.iso}
          >
            <div className={s.rail}>
              <span className={s.dnum}>{group.head.dayNum}</span>
              <span className={s.dmon}>{group.head.monShort}</span>
              <span className={s.ddow}>{group.head.dowShort}</span>
            </div>
            <div className={`${s.dayitems} ${density === 'compact' ? s.compact : s.full}`}>
              {group.items.map(e =>
                density === 'compact' ? (
                  <Row
                    key={e.id}
                    e={e}
                    me={me}
                    onOpen={onOpen}
                    onSave={onSave}
                    onGoing={onGoing}
                   
                  />
                ) : (
                  <Card
                    key={e.id}
                    e={e}
                    me={me}
                    onOpen={onOpen}
                    onSave={onSave}
                    onGoing={onGoing}
                   
                  />
                ),
              )}
            </div>
          </div>
        ))}
      </div>
      {more ? (
        <div ref={tail} className={s.empty}>
          loading…
        </div>
      ) : null}
      {only ? (
        /* A window says so and offers the way out. Without this the list looks like a
           calendar that has lost most of its gigs. */
        <p className={s.windowed}>
          {said}.{' '}
          <button type="button" className={s.clear} onClick={only.clear}>
            {wider}
          </button>
        </p>
      ) : null}
    </>
  )
}
